var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Fragment, createContext, forwardRef, useContext, } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { hideVisually, focusOutline } from '../../styles/style-mixins';
import { uniqueId } from '../../util/id';
import { useClickEvent } from '../../hooks/useClickEvent';
import { deprecate } from '../../util/logger';
import { AccessibilityError } from '../../util/errors';
const baseStyles = ({ theme }) => css `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: var(--cui-bg-normal);
  color: var(--cui-fg-normal);
  text-align: center;
  position: relative;
  border-radius: ${theme.borderRadius.byte};
  transition: box-shadow ${theme.transitions.default};

  &::before {
    display: block;
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${theme.borderRadius.byte};
    border: ${theme.borderWidth.kilo} solid var(--cui-border-normal);
    transition: border ${theme.transitions.default};
  }

  &:hover {
    background-color: var(--cui-bg-normal-hovered);
    color: var(--cui-fg-normal-hovered);

    &::before {
      border-color: var(--cui-border-normal-hovered);
    }
  }

  &:active {
    background-color: var(--cui-bg-normal-pressed);
    color: var(--cui-fg-normal-pressed);

    &::before {
      border-color: var(--cui-border-normal-pressed);
    }
  }
`;
const sizeStyles = ({ theme, size = 'mega' }) => {
    const sizeMap = {
        kilo: {
            padding: `${theme.spacings.bit} ${theme.spacings.mega}`,
        },
        mega: {
            padding: `calc(${theme.spacings.kilo}) ${theme.spacings.giga}`,
        },
        flexible: {
            padding: `${theme.spacings.mega} ${theme.spacings.mega}`,
        },
    };
    return css(sizeMap[size]);
};
const withDescriptionStyles = ({ hasDescription }) => hasDescription &&
    css `
    text-align: start;
    align-items: flex-start;
  `;
const SelectorLabel = styled('label')(baseStyles, sizeStyles, withDescriptionStyles);
const Bold = styled('span') `
  font-weight: ${(p) => p.theme.fontWeight.bold};
`;
const inputStyles = ({ theme }) => css `
  ${hideVisually()};

  &:focus + label::before {
    ${focusOutline()};
  }

  &:focus:not(:focus-visible) + label::before {
    box-shadow: none;
  }

  &:checked + label {
    background-color: var(--cui-bg-accent);

    &::before {
      border: ${theme.borderWidth.mega} solid var(--cui-border-accent);
    }
  }

  &:disabled + label,
  &[disabled] + label {
    pointer-events: none;
    background-color: var(--cui-bg-normal-disabled);
    color: var(--cui-fg-normal-disabled);

    &::before {
      border: ${theme.borderWidth.mega} solid var(--cui-border-normal-disabled);
    }
  }

  &:disabled:checked + label,
  &[disabled]:checked + label {
    background-color: var(--cui-bg-accent-disabled);

    &::before {
      border: ${theme.borderWidth.mega} solid var(--cui-border-accent-disabled);
    }
  }
`;
const SelectorInput = styled('input')(inputStyles);
export const SelectorGroupContext = createContext(false);
/**
 * @deprecated Use the {@link SelectorGroup} component instead.
 */
export const Selector = forwardRef((_a, ref) => {
    var { children, label, description, value, id, name, disabled, multiple, onChange, 'aria-describedby': describedBy, tracking, className, style, size } = _a, props = __rest(_a, ["children", "label", "description", "value", "id", "name", "disabled", "multiple", "onChange", 'aria-describedby', "tracking", "className", "style", "size"]);
    const inputId = id || uniqueId('selector_');
    const descriptionId = description && uniqueId('selector-description_');
    const descriptionIds = [describedBy, descriptionId]
        .filter(Boolean)
        .join(' ');
    const type = multiple ? 'checkbox' : 'radio';
    const handleChange = useClickEvent(onChange, tracking, 'selector');
    const isInsideGroup = useContext(SelectorGroupContext);
    if (process.env.NODE_ENV !== 'production' && !isInsideGroup) {
        deprecate('Selector', 'The Selector component has been deprecated. Use the SelectorGroup component instead.');
    }
    if (process.env.NODE_ENV !== 'production' && children) {
        deprecate('Selector', 'The `children` prop has been deprecated. Use the `label` and `description` props instead.');
    }
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label &&
        !children) {
        throw new AccessibilityError('Selector', 'The `label` prop is missing.');
    }
    const hasDescription = Boolean(description);
    return (_jsxs(Fragment, { children: [_jsx(SelectorInput, Object.assign({ type: type, id: inputId, "aria-describedby": descriptionIds, name: name, value: value, disabled: disabled, 
                // @ts-expect-error Change is handled by onClick for browser support, see https://stackoverflow.com/a/5575369
                onClick: handleChange, 
                // Noop to silence React warning: https://github.com/facebook/react/issues/3070#issuecomment-73311114
                onChange: () => { }, ref: ref }, props)), _jsx(SelectorLabel, Object.assign({ htmlFor: inputId, size: size, className: className, style: style, hasDescription: hasDescription }, { children: hasDescription ? (_jsxs(Fragment, { children: [_jsx(Bold, { children: label || children }), _jsx("span", Object.assign({ "aria-hidden": "true" }, { children: description }))] })) : (label || children) })), hasDescription && (_jsx("p", Object.assign({ id: descriptionId, css: hideVisually }, { children: description })))] }));
});
Selector.displayName = 'Selector';
